import { values } from "lodash";
import debounce from "lodash.debounce";
import React, { useCallback, useState, useEffect } from "react";
import {
  formatDuration,
  intervalToDuration,
  format,
  parse,
  isBefore,
  isAfter,
} from "date-fns";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaBan,
  FaPlusCircle,
} from "react-icons/fa";

// colors
export let Colors = {
  blue: "#0080FF",
  indigo: "#5b5efb",
  purple: "#8927f9",
  pink: "#f24a8b",
  red: "#df3c4e",
  orange: "#f85f32",
  yellow: "#f2bc16",
  green: "#2dc58c",
  teal: "#2acc9c",
  cyan: "#478ffc",
  black: "#000",
  white: "#fff",
  gray: "#6B7280",
  grayDark: "#374151",
  gray100: "#f1f2f7",
  gray200: "#E5E7EB",
  gray300: "#D1D5DB",
  gray400: "#6B7280",
  gray500: "#6B7280",
  gray600: "#4B5563",
  gray700: "#374151",
  gray800: "#1F2937",
  gray900: "#111827",
  primary: "#5f38f9",
  secondary: "#374151",
  success: "#2dc58c",
  info: "#478ffc",
  warning: "#f2bc16",
  danger: "#df3c4e",
  lighter: "#f1f2f7",
  light: "#E5E7EB",
  dark: "#1F2937",
  darker: "#111827",

  bodyColor: "#787c9e",
  bodyBg: "#f8f8f9",
  borderColor: "#d2d4e4",
  borderColorTranslucent: "rgba(0, 0, 0, 0.175)",

  linkColor: "#5f38f9",
  linkHoverColor: "#4c2dc7",
  codeColor: "#f24a8b",
  highlightBg: "#fcf2d0",
};

// list of all months in english word
export let monthList = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

let getToday = function () {
  let today = new Date();
  let dd = String(today.getDate()).padStart(2, "0");
  let mm = String(today.getMonth() + 1).padStart(2, "0");
  let yyyy = today.getFullYear();

  return yyyy + "-" + mm + "-" + dd;
};
export let today = getToday();

let getTesterday = function () {
  let today = new Date();
  let yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  let y_dd = String(yesterday.getDate()).padStart(2, "0");
  let y_mm = String(yesterday.getMonth() + 1).padStart(2, "0");
  let y_yyyy = yesterday.getFullYear();

  return y_yyyy + "-" + y_mm + "-" + y_dd;
};
export let yesterday = getTesterday();

let getCurrentMonth = function () {
  let today = new Date();
  let mm = String(today.getMonth() + 1).padStart(2, "0");
  let yyyy = today.getFullYear();

  return yyyy + "-" + mm;
};

export let currentMonth = getCurrentMonth();

// Get first and last character from string
let getInitials = function (name) {
  if (!name || typeof name !== "string") {
    return "";
  }

  const names = name.trim().split(" ");
  let initials = names[0].substring(0, 1).toUpperCase();

  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }
  return initials;
};

export let toInitials = getInitials;

export let Break = {
  mb: 420,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
  any: Infinity,
};

export function formatUnderscoreString(inputString) {
  return inputString
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
}

export const capitalizeWord = (word) =>
  word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : "";

export const dateRangeOptions = [
  { value: "yesterday", label: "Yesterday" },
  { value: "7_days", label: "Last 7 Days" },
  { value: "15_days", label: "Last 15 Days" },
  { value: "30_days", label: "Last 30 Days" },
  { value: "last_month", label: "Last Month" },
  { value: "3_months", label: "Last 3 Months" },
  { value: "custom", label: "Custom Date" },
];

export const perPageOptions = [
  { value: 10, label: "10 Per Page" },
  { value: 25, label: "25 Per Page" },
  { value: 50, label: "50 Per Page" },
  { value: 100, label: "100 Per Page" },
];

export const reportOption = [
  { label: "Asset Allocation", value: "asset_allocation" },
  { label: "Asset Lifecycle Report ", value: "asset_lifecycle" },
  { label: "Asset Loc/Dep Report ", value: "asset_loc_dep" },
  { label: "Asset End Life Report", value: "asset_end_of_life" },
  { label: "Asset Return", value: "asset_return" },
  { label: "Asset Move Report", value: "asset_movement" },
  { label: "Asset Disposal", value: "asset_disposal" },
  { label: "Maintenance", value: "maintenance" },
  { label: "Depreciation", value: "depreciation" },
  { label: "Vendor Wise", value: "vendor_wise" },
  { label: "Employee Asset", value: "employee_asset" },
];

export function useWindowWidth() {
  const [width, setWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return width;
}

export const userStatusOption = [
  { value: "", label: "Status" },
  { value: "true", label: "Active" },
  { value: "false", label: "InActive" },
];

export const roleDisplayMap = {
  ADMIN: "Administrator",
  HOD: "Head of Department",
  STORE: "Store Manager",
  EMPLOYEE: "Employee",
  AUDITOR: "Auditor",
  LOCATION: "Location Manager",
};

export const roleStyleMap = {
  ADMIN: "bg-soft-primary text-primary",
  HOD: "bg-soft-info text-info",
  STORE: "bg-soft-warning text-warning",
  EMPLOYEE: "bg-soft-success text-success",
  AUDITOR: "bg-soft-danger text-danger",
  LOCATION: "bg-soft-secondary text-secondary",
};

export const unitStatusOption = [
  { value: "IN_STOCK", label: "In Stock" },
  { value: "ALLOCATED", label: "Allocated" },
  { value: "IN_REPAIR", label: "In Repair" },
  { value: "DISPOSED", label: "Disposed" },
];

export const unitMaintenanceOption = [
  { value: "", label: "All" },
  { value: "PREVENTIVE", label: "Preventive" },
  { value: "CORRECTIVE", label: "Corrective" },
];

export const getUnitStatusClassName = (status = "") => {
  const statusKey = status.toLowerCase();

  const statusClasses = {
    in_stock: "badge text-bg-success-soft",
    allocated: "badge text-bg-inf-soft",
    in_repair: "badge text-bg-warning-soft",
    disposed: "badge text-bg-danger-soft",
  };

  return statusClasses[statusKey] || "badge bg-secondary";
};

export function unitStatusLabel(status = "") {
  return status
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function formatDateTimeWithAdd5Hours(datetimeStr) {
  let date;

  // Try to parse "05 Aug 2025, 05:47 AM" format
  if (/[a-zA-Z]{3}/.test(datetimeStr)) {
    // Convert "05 Aug 2025, 05:47 AM" → Date object
    const parts = datetimeStr.split(", ");
    const datePart = parts[0]; // "05 Aug 2025"
    const timePart = parts[1]; // "05:47 AM"

    date = new Date(`${datePart} ${timePart}`);
  } else {
    // Convert "2025-08-05 05:47:00" → Date object
    date = new Date(datetimeStr.replace(" ", "T"));
  }

  if (isNaN(date.getTime())) {
    return "Invalid date";
  }

  // Add 5 hours 30 minutes
  const offsetMillis = (5 * 60 + 30) * 60 * 1000;
  const adjustedDate = new Date(date.getTime() + offsetMillis);

  const options = {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  return adjustedDate.toLocaleString("en-US", options).replace(",", "");
}

export function getFullRoleName(role) {
  if (!role) return "";

  const roleMap = {
    ADMIN: "Admin",
    EMPLOYEE: "Employee",
    HOD: "Head of Department",
    STORE: "Store Manager",
    LOCATION: "Location Manager",
    AUDITOR: "Auditor",
  };

  return roleMap[role] || role;
}

export const getYearsOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];

  for (let year = currentYear; year > currentYear - 5; year--) {
    years.push({
      label: year.toString(),
      value: year,
    });
  }

  return years;
};

export function formatToNumber(value) {
  const num = Number(value);

  if (isNaN(num)) return "00";

  if (num >= 10000000) {
    return (num / 10000000).toFixed(num % 10000000 === 0 ? 0 : 1) + "Cr";
  } else if (num >= 100000) {
    return (num / 100000).toFixed(num % 100000 === 0 ? 0 : 1) + "L";
  } else if (num >= 1000) {
    return (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1) + "K";
  }

  if (num < 10) {
    return `0${num}`;
  }

  return num.toString();
}

export const getTransferStatusBadge = (status) => {
  const normalized = status?.toLowerCase();

  switch (normalized) {
    case "approved":
      return (
        <span className="badge text-bg-success-soft d-inline-flex align-items-center gap-1">
          <FaCheckCircle size={12} /> Approved
        </span>
      );
    case "accepted":
      return (
        <span className="badge text-bg-info-soft d-inline-flex align-items-center gap-1">
          <FaCheckCircle size={12} /> Accepted
        </span>
      );

    case "rejected":
      return (
        <span className="badge text-bg-danger-soft d-inline-flex align-items-center gap-1">
          <FaTimesCircle size={12} /> Rejected
        </span>
      );

    case "pending":
      return (
        <span className="badge text-bg-warning-soft d-inline-flex align-items-center gap-1">
          <FaClock size={12} /> Pending
        </span>
      );

    default:
      return (
        <span className="badge bg-secondary d-inline-flex align-items-center gap-1">
          {status || "Unknown"}
        </span>
      );
  }
};

export const getAssetScanStatusBadge = (status) => {
  const normalized = status?.toLowerCase();

  switch (normalized) {
    case "verified":
      return (
        <span className="rounded-pill badge text-bg-success-soft d-inline-flex align-items-center gap-1">
          <FaCheckCircle size={12} /> {status || "Verified"}
        </span>
      );

    case "misplaced":
      return (
        <span className="rounded-pill badge text-bg-warning-soft d-inline-flex align-items-center gap-1">
          <FaClock size={12} /> Misplaced
        </span>
      );

    case "missing":
      return (
        <span className="rounded-pill badge text-bg-danger-soft d-inline-flex align-items-center gap-1">
          <FaTimesCircle size={12} /> Missing
        </span>
      );

    case "unassigned":
      return (
        <span className="rounded-pill badge bg-dark-subtle d-inline-flex align-items-center gap-1">
          <FaBan size={12} /> Unassigned
        </span>
      );

    case "new":
      return (
        <span className="rounded-pill badge bg-info-subtle d-inline-flex align-items-center gap-1">
          <FaPlusCircle size={12} /> New
        </span>
      );

    default:
      return (
        <span className="rounded-pill badge bg-secondary d-inline-flex align-items-center gap-1">
          {status || "Unknown"}
        </span>
      );
  }
};
